import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Loader2, CheckCircle2, ShieldAlert, AlertOctagon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lightweight no-auth axios client — the /api/public/schedule endpoint must
// stay reachable from cold-email recipients who don't have an admin session.
const publicClient = axios.create({ baseURL: API });

export default function SchedulePage() {
  const [params] = useSearchParams();
  const auditId = params.get("audit") || "";
  const prefilledUrl = params.get("site") || "";

  const [form, setForm] = useState({
    name: "",
    business: "",
    website: prefilledUrl,
    email: "",
    phone: "",
    preferred_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Schedule a Call — ADA Interceptor";
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) {
      setError("Please enter your name.");
      return;
    }
    if (!form.email.includes("@") || form.email.length < 5) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await publicClient.post("/public/schedule", {
        ...form,
        source_audit_id: auditId || undefined,
        source: "schedule_page",
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed. Please try again or email support@adainterceptor.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="schedule-page">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-blue-700 focus:text-white focus:px-4 focus:py-2 focus:font-bold focus:tracking-wider focus:uppercase focus:text-sm focus:ring-2 focus:ring-blue-700 focus:ring-offset-2 focus:rounded-none"
      >
        Skip to main content
      </a>
      {/* Header */}
      <header className="bg-zinc-950 text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <ShieldAlert className="w-6 h-6 text-blue-400" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">
              Confidential · B2B
            </div>
            <div className="font-display text-xl font-black tracking-tight">
              ADA INTERCEPTOR
            </div>
          </div>
        </div>
      </header>

      <main id="main" className="max-w-3xl mx-auto px-6 py-10">
        {!submitted ? (
          <>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] text-zinc-950">
              Schedule a 10-minute briefing.
            </h1>
            <p className="mt-4 text-base text-zinc-700 max-w-2xl leading-relaxed">
              We&apos;ll walk you through the specific WCAG / ADA findings on your site,
              show you the plaintiff-firm exposure surface, and quote what a fully-managed
              Silent Shield deployment looks like. No hard sell — just the report.
            </p>

            {auditId && (
              <div
                className="mt-5 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs font-mono text-blue-900"
                data-testid="schedule-audit-context"
              >
                Audit reference: {auditId.slice(0, 8)}
              </div>
            )}

            <form
              onSubmit={submit}
              className="mt-8 bg-white border border-zinc-950 p-6 sm:p-8 space-y-5"
              data-testid="schedule-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">
                    Full name <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Operator"
                    className="mt-1 rounded-sm h-11"
                    required
                    data-testid="schedule-name-input"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">
                    Email <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@business.com"
                    className="mt-1 rounded-sm h-11"
                    required
                    data-testid="schedule-email-input"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">
                    Business name
                  </Label>
                  <Input
                    value={form.business}
                    onChange={update("business")}
                    placeholder="Acme Corp."
                    className="mt-1 rounded-sm h-11"
                    data-testid="schedule-business-input"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">
                    Website
                  </Label>
                  <Input
                    value={form.website}
                    onChange={update("website")}
                    placeholder="https://yourbusiness.com"
                    className="mt-1 rounded-sm h-11 font-mono text-sm"
                    data-testid="schedule-website-input"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">
                    Phone
                  </Label>
                  <Input
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(555) 123-4567"
                    className="mt-1 rounded-sm h-11"
                    data-testid="schedule-phone-input"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">
                    Preferred time
                  </Label>
                  <Input
                    value={form.preferred_time}
                    onChange={update("preferred_time")}
                    placeholder="Weekday mornings, EST"
                    className="mt-1 rounded-sm h-11"
                    data-testid="schedule-time-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">
                  Anything specific you&apos;d like to discuss?
                </Label>
                <Textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Optional — questions, concerns, timeline, etc."
                  className="mt-1 rounded-sm font-mono text-sm"
                  data-testid="schedule-message-input"
                />
              </div>

              {error && (
                <div
                  className="bg-red-50 border border-red-300 p-3 text-sm text-red-800 flex items-start gap-2"
                  data-testid="schedule-error"
                >
                  <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" />
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto rounded-sm h-12 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider px-8"
                data-testid="schedule-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Schedule 10 Minute Call"
                )}
              </Button>

              <p className="text-[11px] text-zinc-500 leading-snug">
                Your information is used solely to schedule this briefing. We never share
                or sell contact data. Reply STOP to any future email to opt out.
              </p>
            </form>
          </>
        ) : (
          <div
            className="bg-white border border-emerald-600 p-8 sm:p-12 text-center"
            data-testid="schedule-success"
          >
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-zinc-950">
              Got it — we&apos;ll be in touch shortly.
            </h1>
            <p className="mt-4 text-base text-zinc-700 max-w-xl mx-auto leading-relaxed">
              Your request landed in our queue. You&apos;ll hear back from{" "}
              <span className="font-mono font-bold">support@adainterceptor.com</span>{" "}
              within one business day to confirm a time.
            </p>
            <p className="mt-6 text-xs text-zinc-500 font-mono">
              Reference: {form.email}
            </p>
          </div>
        )}
      </main>

      <footer className="mt-16 border-t border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6 text-[11px] text-zinc-500 flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} ADA Interceptor. Confidential B2B.</span>
          <a href="mailto:support@adainterceptor.com" className="hover:text-zinc-950">
            support@adainterceptor.com
          </a>
        </div>
      </footer>
    </div>
  );
}
